import React from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import {
  FORCE_OPTIONS,
  PUSH_STATUS,
  PUSH_STATUS_OPTIONS,
  SORT_OPTIONS,
  VIDEO_SOURCE_OPTIONS,
} from "./constants";

// Status tabs, in the order an approver works through them: the queue that
// needs them first, then the two outcomes, then everything.
const STATUS_TABS = [
  { value: "PENDING_APPROVAL", label: "Awaiting Approval", color: "#8b5cf6" },
  { value: "APPROVED", label: "Approved", color: "#2563eb" },
  { value: "REJECTED", label: "Rejected", color: "#dc2626" },
  { value: "ALL", label: "All", color: "#475569" },
];

function StatusTab({ tab, active, count, onClick }) {
  return (
    <Box
      role="tab"
      aria-selected={active}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.9,
        px: 1.6,
        py: 0.85,
        borderRadius: 2,
        cursor: "pointer",
        userSelect: "none",
        transition: "background-color .15s, box-shadow .15s, color .15s",
        color: active ? tab.color : "text.secondary",
        bgcolor: active ? "#fff" : "transparent",
        boxShadow: active ? "0 1px 2px rgba(15,23,42,.08), 0 0 0 1px rgba(15,23,42,.06)" : "none",
        "&:hover": { bgcolor: active ? "#fff" : "rgba(15,23,42,.04)" },
        "&:focus-visible": { outline: `2px solid ${tab.color}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          flexShrink: 0,
          bgcolor: active ? tab.color : "#cbd5e1",
        }}
      />
      <Typography variant="body2" sx={{ fontWeight: active ? 700 : 500, whiteSpace: "nowrap", fontSize: 13 }}>
        {tab.label}
      </Typography>
      {count != null && (
        <Box
          sx={{
            minWidth: 20,
            px: 0.6,
            borderRadius: 1,
            textAlign: "center",
            fontSize: 11,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            lineHeight: "17px",
            color: active ? tab.color : "#64748b",
            bgcolor: active ? `${tab.color}14` : "rgba(15,23,42,.06)",
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

export default function ApprovalToolbar({
  filter,
  onFilterChange,
  counts,
  search,
  onSearchChange,
  feedFilter,
  onFeedFilterChange,
  feedOptions,
  videoSourceFilter,
  onVideoSourceFilterChange,
  pushStatusFilter,
  onPushStatusFilterChange,
  forceFilter,
  onForceFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  filtersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  onClearFilters,
  shown,
  total,
  countdown,
  loading,
  onRefresh,
}) {
  return (
    <Box
      sx={{
        position: "sticky",
        // Clears SideDrawer's fixed 65px AppBar — at top:0 this would scroll
        // underneath it and disappear.
        top: 65,
        zIndex: 3,
        mb: 2,
        pt: 1.25,
        bgcolor: "#fff",
        borderBottom: "1px solid",
        borderColor: "#eef2f6",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1.5} flexWrap="wrap" mb={1.5}>
        <Box
          role="tablist"
          sx={{ display: "flex", gap: 0.4, p: 0.4, borderRadius: 2.5, bgcolor: "#f1f5f9", flexWrap: "wrap" }}
        >
          {STATUS_TABS.map((tab) => (
            <StatusTab
              key={tab.value}
              tab={tab}
              active={filter === tab.value}
              count={counts?.[tab.value]}
              onClick={() => onFilterChange(tab.value)}
            />
          ))}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="This view reloads on its own so a render finishing (or a push being sent) shows up without a click.">
            <Box
              sx={{
                px: 1.1,
                py: 0.45,
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: 0.7,
                cursor: "help",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#16a34a",
                  animation: "apPulse 2s infinite",
                  "@keyframes apPulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                Refreshing in {countdown}s
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Refresh now">
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={filtersOpen ? 1.5 : 1.5}>
        <TextField
          size="small"
          placeholder="Search title or video key…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            flex: "1 1 260px",
            maxWidth: 420,
            "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange("")} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Badge badgeContent={activeFilterCount} color="primary" overlap="rectangular">
          <Button
            size="small"
            variant={filtersOpen ? "contained" : "outlined"}
            color={filtersOpen ? "primary" : "inherit"}
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => onFiltersOpenChange(!filtersOpen)}
            sx={{ borderRadius: 2, textTransform: "none", borderColor: "#e2e8f0" }}
          >
            Filters
          </Button>
        </Badge>

        <TextField
          select
          size="small"
          value={sort}
          onChange={(e) => onSortChange(e.target.value)}
          sx={{ minWidth: 152, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && onViewChange(v)}
          sx={{ "& .MuiToggleButton-root": { borderRadius: 2, px: 1.1, borderColor: "#e2e8f0" } }}
        >
          <ToggleButton value="cards" aria-label="Card view">
            <Tooltip title="Card view — thumbnail-first, best for judging the video itself">
              <ViewModuleIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <Tooltip title="Table view — dense, best for scanning many pushes">
              <ViewListIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        <Box flex={1} />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          Showing <b>{shown}</b> of {total}
        </Typography>
      </Box>

      <Collapse in={filtersOpen} unmountOnExit>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
            p: 1.25,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: "#f8fafc",
            border: "1px solid #eef2f6",
          }}
        >
          <TextField
            select
            size="small"
            label="Feed"
            value={feedFilter}
            onChange={(e) => onFeedFilterChange(e.target.value)}
            sx={{ minWidth: 190, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          >
            <MenuItem value="ALL">All feeds</MenuItem>
            {feedOptions.map((label) => (
              <MenuItem key={label} value={label}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Video source"
            value={videoSourceFilter}
            onChange={(e) => onVideoSourceFilterChange(e.target.value)}
            sx={{ minWidth: 160, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          >
            {VIDEO_SOURCE_OPTIONS.map((v) => (
              <MenuItem key={v} value={v}>
                {v === "ALL" ? "All sources" : v}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Push status"
            value={pushStatusFilter}
            onChange={(e) => onPushStatusFilterChange(e.target.value)}
            sx={{ minWidth: 170, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          >
            {PUSH_STATUS_OPTIONS.map((v) => (
              <MenuItem key={v} value={v}>
                {v === "ALL" ? "All statuses" : PUSH_STATUS[v]?.label || v}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Send mode"
            value={forceFilter}
            onChange={(e) => onForceFilterChange(e.target.value)}
            sx={{ minWidth: 140, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          >
            {FORCE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          {activeFilterCount > 0 && (
            <Chip
              size="small"
              label={`Clear ${activeFilterCount} filter${activeFilterCount !== 1 ? "s" : ""}`}
              onDelete={onClearFilters}
              onClick={onClearFilters}
              sx={{ ml: "auto", fontWeight: 600 }}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
